<template>
  <div :class="{'accordion-list__item-content': advanced}" style="min-width: 0;margin-top: 12px;">
    <h5 class="" v-translate translate-context="event_gallery">Price list</h5>
    <p class="text-help-dark">
      <translate translate-context="event_gallery.subtitle">Define the prices of your photos for sale</translate>
    </p>
    <template v-if="advanced">
      <CRow>
        <CCol lg="7">
          <h6 class="font-weight-semi-bold">{{ $pgettext('event_wizard.price_list.form', 'Display name') }}</h6>
          <CInput v-model="form.display_name" @input="onPreview"/>
        </CCol>
        <CCol lg="5">
          <h6 class="font-weight-semi-bold">{{ $pgettext('event_wizard.price_list.form', 'Validity Period') }}</h6>
          <VueCtkDateTimePicker
            v-model="validity"
            :no-label="true"
            :label="$gettext('Select date')"
            format="YYYY-MM-DD HH:mm:ss"
            formatted="LL"
            range
            :right="true"
            :only-date="true"
            :no-button-now="true"
            color="#1867FF"
            @input="onPreview"
          />
        </CCol>
      </CRow>
    </template>

    <div style="position: relative; height: 200px;">
      <div class="form-option-w" style="">
        <div class="form-option"
             v-for="typeOption in cardOptions" :key="typeOption.id"
             @click.prevent.stop="onSelectType(typeOption.id)"
             @mouseenter="onHoverType(typeOption.id)"
             @mouseleave="onLeaveType"
             :class="{'active': typeOption.id === form.configuration.type}">
          <div class="title">
            <CIcon :name="typeOption.icon" size="custom-size" :height="20" class="mb-2"/>
            <span class="name">{{ typeOption.name }}</span>
            <CBadge v-if="typeOption.id === activeType" color="success" class="badge-outline">
             <CIcon name="cipDot" size="custom-size" :height="6" /> {{ $pgettext('event_gallery.price_list', 'Active') }}
            </CBadge>
          </div>
          <div class="descr">{{ typeOption.description }}</div>
        </div>
      </div>
    </div>
    <hr class="mt-2 mb-2"/>
    <PackPriceList
      :currency="eventCurrency"
      :form_in_line="advanced"
      @update="onPreviewPriceWithSettings"
      ref="prices"
      v-if="form.configuration.type === priceListType.pack"
    />
    <UnitPriceList
      :currency="eventCurrency"
      @update="onPreviewPriceWithSettings"
      ref="prices"
      v-if="form.configuration.type === priceListType.unit"
    />
    <CustomPriceList
      :currency="eventCurrency"
      @update="onPreviewPriceWithSettings"
      ref="prices"
      v-if="form.configuration.type === priceListType.custom"
    />
    <ParentPriceList
      ref="prices"
      @update="onPreviewPrice"
      v-if="form.configuration.type === priceListType.parent"
    />
  </div>
</template>

<script>

import 'vue2-timepicker/dist/VueTimepicker.css'
import CustomPriceList from "@/domain/eventGallery/tab/priceList/CustomPriceList.vue";
import priceListType from "@/domain/core/constant/priceListType";
import PackPriceList from "@/domain/eventGallery/tab/priceList/PackPriceList.vue";
import UnitPriceList from "@/domain/eventGallery/tab/priceList/UnitPriceList.vue";
import ParentPriceList from "@/domain/eventGallery/tab/priceList/ParentPriceList.vue";
import {mapGetters} from "vuex";
import {isEqual, cloneDeep} from "lodash";

export default {
  name: "PriceListOpened",
  emits: ['update', 'preview', 'dirty'],
  components: {
    ParentPriceList,
    UnitPriceList,
    PackPriceList,
    CustomPriceList
  },
  data() {
    return {
      typeCache: {},
      activeType: null,
      savedSnapshot: null,
      validity: {
        start: null,
        end: null,
      },
      form: {
        items: [],
        configuration: {},
        valid_from: null,
        valid_to: null,
        allow_preorder: false,
        allow_upsell: false,
        cta_enabled: false,
        cta_min_qty: 0,
        cta_position: 'b',
        display_name: '',
      },
      ctaPositions: [
        {id: 'b', name: this.$pgettext('event_wizard.price_list.form.label', 'Bottom')},
        {id: 't', name: this.$pgettext('event_wizard.price_list.form.label', 'Top')},
      ],
    }
  },
  watch: {
    data: function () {
      this.onChangeData()
    }
  },
  mounted() {
    this.onChangeData()
  },
  computed: {
    ...mapGetters('event', ['eventCurrency']),
    priceListType() {
      return priceListType
    },
    cardOptions() {
      let options = [
        {
          id: 'unit',
          name: this.$pgettext('event_gallery.price_list.type', 'Sale by unit'),
          description: this.$pgettext('event_gallery.price_list.type', 'Set the unit price of each single photo'),
          icon: 'cipPicture'
        },
        {
          id: 'pack',
          name: this.$pgettext('event_gallery.price_list.type', 'Complete package sale'),
          description: this.$pgettext('event_gallery.price_list.type', 'Select the number of photos in the package. Price per photo calculated automatically.'),
          icon: 'cipLayersThree01'
        },
      ]
      if (this.isSubGallery) {
        options.push({
          id: 'parent',
          name: this.$pgettext('event_gallery.price_list.type', 'Parent'),
          description: this.$pgettext('event_gallery.price_list.type', 'Use parent gallery price list'),
          icon: 'cipLayersThree01'
        })
      }
      if (this.advanced) {
        options.push({
          id: 'custom',
          name: this.$pgettext('event_gallery.price_list.type', 'Custom'),
          description: this.$pgettext('event_gallery.price_list.type', 'Create a fully custom price list'),
          icon: 'cipPencil'
        })
      }
      return options
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    advanced: {
      type: Boolean,
      required: true
    },
    isSubGallery: {
      type: Boolean,
      required: true
    }
  },
  methods: {
    onSelectType(newType) {
      if (newType === this.form.configuration.type) return
      this.onChangeLayoutType({value: newType})
    },
    getDemoData(typeId) {
      const demoItems = {
        pack: [
          {qty: 1, price: 5},
          {qty: 2, price: 12},
          {qty: 3, price: 18},
          {qty: 4, price: 25},
          {qty: 5, price: 30},
        ],
        unit: [
          {from_qty: 1, to_qty: 9999, unit_price: 5},
        ],
      }
      return {
        items: demoItems[typeId] || demoItems.pack,
        configuration: {
          type: typeId,
          ...(typeId === 'pack' ? {complete_set_num: 5, first_photo_price: 5, complete_set_price: 30} : {}),
          ...(typeId === 'unit' ? {unit_price: 5} : {}),
        },
        isDemo: true,
      }
    },
    onHoverType(typeId) {
      if (typeId === this.activeType && typeId === this.form.configuration.type) return
      if (typeId === this.activeType) {
        this.$emit('preview', {
          ...this.data,
          valid_from: this.data.valid_from,
          valid_to: this.data.valid_to,
        })
        return
      }
      this.$emit('preview', this.getDemoData(typeId))
    },
    onLeaveType() {
      this.onPreview()
    },
    isValid() {
      if (!this.$refs.prices) return false
      if (typeof this.$refs.prices.isValid === 'function') return this.$refs.prices.isValid()
      return true
    },
    getPriceList() {
      const priceList = this.$refs.prices.getPriceList()
      this.form.items = priceList.items
      this.form.configuration = {
        ...priceList.configuration,
        type: this.form.configuration.type
      }

      // Se il componente Custom restituisce i settings, li aggiorna
      if (priceList.settings) {
        this.form.allow_preorder = priceList.settings.allow_preorder
        this.form.allow_upsell = priceList.settings.allow_upsell
        this.form.cta_enabled = priceList.settings.cta_enabled
        this.form.cta_min_qty = priceList.settings.cta_min_qty
        this.form.cta_position = priceList.settings.cta_position
      }

      // Al salvataggio: il tipo corrente diventa attivo, cancella il cache degli altri
      this.activeType = this.form.configuration.type
      this.typeCache = {}

      return {
        ...this.form,
        valid_from: this.validity.start,
        valid_to: this.validity.end,
      }
    },
    async onChangeData() {
      this.form = {
        ...this.data,
      }
      if (!this.form.configuration)
        this.form.configuration = {type: priceListType.default(this.isSubGallery)}
      if (!this.form.configuration.type)
        this.form.configuration.type = priceListType.default(this.isSubGallery)

      this.activeType = (this.data.items && this.data.items.length > 0) ? this.form.configuration.type : null
      this.typeCache = {}

      this.validity = {
        start: this.data.valid_from,
        end: this.data.valid_to,
      }

      await this.initPrices()
      this.saveSnapshot()
    },
    saveSnapshot() {
      this.$nextTick(() => {
        if (!this.$refs.prices) return
        const priceList = this.$refs.prices.getPriceList()
        this.savedSnapshot = {
          type: this.form.configuration.type,
          items: cloneDeep(priceList.items),
          configuration: cloneDeep(priceList.configuration),
          settings: priceList.settings ? cloneDeep(priceList.settings) : null,
        }
        this.$emit('dirty', false)
      })
    },
    emitDirty() {
      this.$nextTick(() => {
        if (!this.savedSnapshot) {
          this.$emit('dirty', true)
          return
        }
        if (!this.$refs.prices) {
          this.$emit('dirty', false)
          return
        }
        const current = this.$refs.prices.getPriceList()
        const isDirty = !isEqual(current.items, this.savedSnapshot.items)
          || !isEqual(current.configuration, this.savedSnapshot.configuration)
        this.$emit('dirty', isDirty)
      })
    },
    onPreviewPrice({items, configuration}) {
      this.form.items = items
      this.form.configuration = {
        ...configuration,
        type: this.form.configuration.type
      }
      this.onPreview()
      this.emitDirty()
    },
    onPreviewPriceWithSettings({items, configuration, settings}) {
      this.form.items = items
      this.form.configuration = {
        ...configuration,
        type: this.form.configuration.type
      }
      // Aggiorna i settings dal componente Custom
      if (settings) {
        this.form.allow_preorder = settings.allow_preorder
        this.form.allow_upsell = settings.allow_upsell
        this.form.cta_enabled = settings.cta_enabled
        this.form.cta_min_qty = settings.cta_min_qty
        this.form.cta_position = settings.cta_position
      }
      this.onPreview()
      this.emitDirty()
    },
    async onPreview() {
      if (!this.form.items || this.form.items.length === 0) {
        this.$emit('preview', this.getDemoData(this.form.configuration.type))
        return
      }
      this.$emit('preview', {
        ...this.form,
        valid_from: this.validity.start,
        valid_to: this.validity.end,
      })
    },
    async onChangeLayoutType(newTypeObj) {
      const newType = newTypeObj.value
      const oldType = this.form.configuration.type

      // Salva i dati del tipo corrente nella cache prima di switchare
      if (oldType && this.$refs.prices) {
        const currentData = this.$refs.prices.getPriceList()
        this.typeCache[oldType] = {
          items: currentData.items,
          configuration: currentData.configuration,
          settings: currentData.settings || null,
        }
      }

      // Ripristina dalla cache se disponibile, altrimenti parti pulito
      const cached = this.typeCache[newType]
      if (cached) {
        this.form.items = cached.items
        this.form.configuration = {
          ...cached.configuration,
          type: newType
        }
        if (cached.settings) {
          this.form.allow_preorder = cached.settings.allow_preorder
          this.form.allow_upsell = cached.settings.allow_upsell
          this.form.cta_enabled = cached.settings.cta_enabled
          this.form.cta_min_qty = cached.settings.cta_min_qty
          this.form.cta_position = cached.settings.cta_position
        }
      } else {
        this.form.items = []
        this.form.configuration = { type: newType }
      }

      await this.initPrices(true)
      this.saveSnapshot()
      this.onPreview()
    },
    async initPrices(onChange = false) {
      await this.$nextTick()
      await this.delay(100)
      if (!this.$refs.prices) return

      const initData = {
        items: this.form.items,
        configuration: this.form.configuration,
        onChange: onChange
      }

      // Passa i settings correnti solo al componente Custom
      if (this.form.configuration.type === priceListType.custom) {
        initData.parentSettings = {
          allow_preorder: this.form.allow_preorder,
          allow_upsell: this.form.allow_upsell,
          cta_enabled: this.form.cta_enabled,
          cta_min_qty: this.form.cta_min_qty,
          cta_position: this.form.cta_position
        }
      }

      this.$refs.prices.init(initData)
    }
  }
}
</script>